import type { ServerMetadata } from "@app/lib/actions/mcp_internal_actions/tool_definition";
import { cosAgentNameMaxLength } from "@app/types/cos_agent_name";
import { COS_TONES } from "@app/types/cos_tone";
import { z } from "zod";

export const MEMOAIR_APP_SERVER_NAME = "memoair_app" as const;
export const MEMOAIR_APP_LIST_CONNECTIONS_TOOL_NAME = "list_connections";
export const MEMOAIR_APP_CONNECT_TOOL_NAME = "connect";
export const MEMOAIR_APP_DISCONNECT_TOOL_NAME = "disconnect";
export const MEMOAIR_APP_LIST_PROJECTS_TOOL_NAME = "list_projects";
export const MEMOAIR_APP_CREATE_AGENT_TOOL_NAME = "create_agent";
export const MEMOAIR_APP_LIST_PENDING_APPROVALS_TOOL_NAME =
  "list_pending_approvals";
export const MEMOAIR_APP_DECIDE_APPROVAL_TOOL_NAME = "decide_approval";
export const MEMOAIR_APP_SEARCH_CONVERSATIONS_TOOL_NAME =
  "search_conversations";
export const MEMOAIR_APP_SET_PREFERENCE_TOOL_NAME = "set_preference";
export const MEMOAIR_APP_LIST_CONVERSATIONS_TOOL_NAME = "list_my_conversations";
export const MEMOAIR_APP_READ_CONVERSATION_TOOL_NAME = "read_conversation";
export const MEMOAIR_APP_PROJECTS_OVERVIEW_TOOL_NAME = "projects_overview";
export const MEMOAIR_APP_HOME_OVERVIEW_TOOL_NAME = "home_overview";
export const MEMOAIR_APP_LIST_MEMBERS_TOOL_NAME = "list_members";
export const MEMOAIR_APP_LIST_LIBRARY_TOOL_NAME = "list_library";

export const MEMOAIR_APP_TOOLS_METADATA = [
  {
    name: MEMOAIR_APP_LIST_CONNECTIONS_TOOL_NAME,
    description:
      "List the apps and tools the current user has connected to MemoAir and the ones they could connect: first-party apps (Gmail, Google Drive, Slack, ...), remote MCP tools (installed ones and presets such as Granola), and Composio apps (Linear, Jira, ...), each with a `personal` or `workspace` scope. Status `needs_admin` means a workspace admin has to turn the app on first. Use this when the user asks what they have connected; when they name one app, call connect for it instead. A Composio entry with status `unknown` means Composio could not be reached just now.",
    schema: {},
    stake: "never_ask",
    eager: true,
    displayLabels: {
      running: "Checking your connections",
      done: "Check your connections",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    name: MEMOAIR_APP_CONNECT_TOOL_NAME,
    description:
      "Connect the app the user named, or confirm it is already connected. Call this first whenever the user names an app. Returns `connected` (use that app's tools), shows the user a card in the conversation to connect it, or for an admin to turn it on (the turn resumes once they act on it), returns a `connect_link` to give the user, or `unsupported` (MemoAir can't connect that app yet).",
    schema: {
      provider: z
        .string()
        .describe(
          "App to connect, as the user named it or as list_connections names it (e.g. Gmail, Granola, linear)."
        ),
      reason: z
        .string()
        .optional()
        .describe(
          'What you need the app for, as a few words that finish "needs Granola to ..." (e.g. "check your meeting notes"). Shown on the card.'
        ),
    },
    stake: "never_ask",
    eager: true,
    displayLabels: {
      running: "Connecting the app",
      done: "Connect the app",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    name: "list_agents",
    description:
      "List the agents the current user can use in this workspace, with what each can reach (`capabilities`: the tool servers it is equipped with, e.g. gmail, slack) and whether it is `reachable` right now. Call this before recommending or handing work to an agent.",
    schema: {},
    stake: "never_ask",
    displayLabels: {
      running: "Listing your agents",
      done: "List your agents",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    name: MEMOAIR_APP_CREATE_AGENT_TOOL_NAME,
    description:
      "Create a dedicated agent for a standing duty, unpublished and owned by the user. Only after the user said yes to it. An agent with the same name is returned instead of a second one. Returns a builder link where they can add tools and skills.",
    schema: {
      name: z
        .string()
        .min(1)
        .max(64)
        .regex(/^[A-Za-z0-9_-]+$/, "letters, digits, - and _ only")
        .describe("A short handle, no spaces: 'OnboardingGuide'."),
      description: z
        .string()
        .min(1)
        .max(300)
        .describe("One line on the duty it owns."),
      instructions: z
        .string()
        .min(1)
        .max(8000)
        .describe(
          "Who it is, the duty, when it acts, and what it must never do without the user's approval."
        ),
    },
    stake: "low",
    displayLabels: {
      running: "Creating an agent",
      done: "Created an agent",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    name: MEMOAIR_APP_LIST_PROJECTS_TOOL_NAME,
    description:
      "List the projects (Pods) the current user is a member of, with a link to each. Works in any conversation. When `total` is larger than the projects listed, say how many there are.",
    schema: {},
    stake: "never_ask",
    displayLabels: {
      running: "Listing your projects",
      done: "List your projects",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    name: MEMOAIR_APP_DISCONNECT_TOOL_NAME,
    description:
      "Disconnect an app the current user connected personally (Gmail, Granola, ...). Workspace-level connections and Composio apps are managed from Settings; for those it says where.",
    schema: {
      provider: z
        .string()
        .describe("App to disconnect, as list_connections names it."),
    },
    stake: "low",
    displayLabels: {
      running: "Disconnecting the app",
      done: "Disconnect the app",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    name: MEMOAIR_APP_LIST_PENDING_APPROVALS_TOOL_NAME,
    description:
      "List the agent actions waiting for the current user's approval across all their conversations: which tool, with which arguments, in which conversation. Call this when they ask what is waiting on them, what is blocked, or what needs approving.",
    schema: {},
    stake: "never_ask",
    displayLabels: {
      running: "Checking what awaits your approval",
      done: "Check what awaits your approval",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    name: MEMOAIR_APP_DECIDE_APPROVAL_TOOL_NAME,
    description:
      "Approve or reject one agent action waiting on the current user, as list_pending_approvals identified it. Only on their explicit say-so for that exact action; an optional note lands in the conversation as theirs.",
    schema: {
      actionId: z
        .string()
        .describe("The action's `actionId` from list_pending_approvals."),
      approved: z.enum(["approved", "rejected"]),
      note: z.string().max(2000).optional(),
    },
    stake: "high",
    displayLabels: {
      running: "Recording your decision",
      done: "Record your decision",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    name: MEMOAIR_APP_SEARCH_CONVERSATIONS_TOOL_NAME,
    description:
      "Search the current user's own conversations by title and return links to the matches, newest first. Use when they ask to find a past chat or what was discussed about something.",
    schema: {
      query: z.string().min(1).describe("Words from the conversation's title."),
    },
    stake: "never_ask",
    displayLabels: {
      running: "Searching your conversations",
      done: "Search your conversations",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    name: MEMOAIR_APP_LIST_CONVERSATIONS_TOOL_NAME,
    description:
      "List every conversation the current user takes part in, newest first, with totals by kind: plain chats, direct messages, and project threads. Filter by title words or by project. Call this for how many chats or threads they have, which is the latest, or what is in a project.",
    schema: {
      q: z.string().optional().describe("Words from the title."),
      project: z
        .string()
        .optional()
        .describe("A project's name or sId, to list only its threads."),
      limit: z.number().int().min(1).max(100).optional().default(30),
    },
    stake: "never_ask",
    displayLabels: {
      running: "Listing your conversations",
      done: "List your conversations",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    name: MEMOAIR_APP_READ_CONVERSATION_TOOL_NAME,
    description:
      "Read the messages of one conversation the current user can see, most recent last, with who said what and when. Use after list_my_conversations or search_conversations to answer what was said or decided in a thread.",
    schema: {
      conversationId: z.string().describe("The conversation's sId."),
      lastMessages: z
        .number()
        .int()
        .min(1)
        .max(200)
        .optional()
        .default(40)
        .describe("How many of the most recent messages to read."),
    },
    stake: "never_ask",
    displayLabels: {
      running: "Reading a conversation",
      done: "Read a conversation",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    name: MEMOAIR_APP_PROJECTS_OVERVIEW_TOOL_NAME,
    description:
      "Summarize every project the current user is a member of in one call: open and done task counts, how many threads it has and how many are unread, when it was last active, and its most recent threads. Call this for progress, status or what-changed questions across projects; read a thread with read_conversation for the detail.",
    schema: {},
    stake: "never_ask",
    displayLabels: {
      running: "Reviewing your projects",
      done: "Review your projects",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    name: MEMOAIR_APP_HOME_OVERVIEW_TOOL_NAME,
    description:
      "Summarize what the current user's Home shows right now in one call: today's agenda (meetings, due tasks, routines about to run), actions waiting for their approval, tasks waiting for their review and open tasks, what happened recently, and the latest morning briefing. Call this for what's my day, what needs me, what's on my Home, or what changed.",
    schema: {
      days: z
        .number()
        .int()
        .min(1)
        .max(8)
        .optional()
        .default(1)
        .describe("How many days of agenda to include, starting today."),
    },
    stake: "never_ask",
    displayLabels: {
      running: "Reading your Home",
      done: "Read your Home",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    name: MEMOAIR_APP_LIST_MEMBERS_TOOL_NAME,
    description:
      "List the people in the current user's workspace with their role, or the members of one project when a project is given. Call this for who is in a project, who is on the team, or to find a colleague's name or email.",
    schema: {
      projectId: z
        .string()
        .optional()
        .describe(
          "The project's `sId` as list_projects or projects_overview returns it (its name also works). Omit for the whole workspace."
        ),
    },
    stake: "never_ask",
    displayLabels: {
      running: "Listing people",
      done: "List people",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    name: MEMOAIR_APP_LIST_LIBRARY_TOOL_NAME,
    description:
      "List the artifacts in the current user's Library (documents, dashboards, frames, files agents produced), newest first, optionally filtered by words from the file name. Call this to find something an agent made earlier.",
    schema: {
      q: z.string().optional().describe("Words from the file name."),
      limit: z.number().int().min(1).max(50).optional().default(20),
    },
    stake: "never_ask",
    displayLabels: {
      running: "Searching your Library",
      done: "Search your Library",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    name: MEMOAIR_APP_SET_PREFERENCE_TOOL_NAME,
    description:
      "Set one of the current user's chief-of-staff preferences: what to call them (`preferredName`), the tone to use (`tone`), or the agent's own name (`agentName`). Applies from the next message.",
    schema: {
      preferredName: z.string().min(1).max(60).optional(),
      tone: z.enum(COS_TONES).optional(),
      agentName: z.string().min(1).max(cosAgentNameMaxLength).optional(),
    },
    stake: "never_ask",
    displayLabels: {
      running: "Saving your preference",
      done: "Save your preference",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
] as const;

export const MEMOAIR_APP_SERVER = {
  serverInfo: {
    name: MEMOAIR_APP_SERVER_NAME,
    version: "1.0.0",
    description:
      "See and act on the MemoAir app for the current user: their connections, connecting and disconnecting apps, the agents they can use, their projects, actions waiting for their approval, their past conversations and their preferences.",
    authorization: null,
    icon: "ActionCheckCircleIcon",
    documentationUrl: null,
  },
  tools: MEMOAIR_APP_TOOLS_METADATA,
} as const satisfies ServerMetadata;
